import React, { useState, Fragment } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Download, Info, Ship, ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';
import { ContactForm } from '../ContactForm';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
// Filtered barge database with only company barges
const bargeDB = [{
  id: 'jmc187',
  name: 'JMC187 - 180x54',
  type: 'Standard Deck',
  workableLength: 54.369,
  workableWidth: 14.78,
  loadlineCapacity: 803.47,
  image: 'https://images.unsplash.com/photo-1520931061294-db3e762a9273?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80',
  description: 'Standard deck barge for general cargo transport.'
}, {
  id: 'og541',
  name: 'OG541 - 260x54',
  type: 'Ocean-Going',
  workableLength: 78.825,
  workableWidth: 15.25,
  loadlineCapacity: 1202.08,
  image: 'https://images.unsplash.com/photo-1564466809058-bf4114d55352?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80',
  description: 'Large ocean-going barge for heavy project cargo.'
}, {
  id: 'og721',
  name: 'OG721 - 260x72',
  type: 'Heavy Lift',
  workableLength: 74.239,
  workableWidth: 20.95,
  loadlineCapacity: 1555.01,
  image: 'https://images.unsplash.com/photo-1566887148-8e7d8390397f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=400&q=80',
  description: 'Our largest heavy lift barge for the most demanding projects.'
}];
export function ResultsStep({
  formData
}) {
  const [expandedBarge, setExpandedBarge] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(true);
  const scrollToContactForm = () => {
    const contactForm = document.getElementById('contact-form');
    if (contactForm) {
      contactForm.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const summaryData = {
    totalWeight: formData.tableData?.reduce((sum, row) => sum + (row.totalWeight || 0), 0) || 0,
    totalArea: formData.tableData?.reduce((sum, row) => sum + (row.totalArea || 0), 0) || 0
  };
  const stowageFactor = formData.stowageFactor || 1.3;
  const calculateBargeResults = barge => {
    const workableArea = barge.workableLength * barge.workableWidth;
    const workableAreaWithStowage = workableArea / stowageFactor;
    const weightLimitingQuantity = summaryData.totalWeight / barge.loadlineCapacity;
    const areaLimitingQuantity = summaryData.totalArea / workableAreaWithStowage;
    // Calculate the number of barges needed (round up to nearest whole barge)
    const numBargesRequired = Math.ceil(Math.max(weightLimitingQuantity, areaLimitingQuantity));
    const limitingFactor = weightLimitingQuantity > areaLimitingQuantity ? 'Weight' : 'Area';
    const loadlinePercentage = summaryData.totalWeight / (numBargesRequired * barge.loadlineCapacity) * 100;
    // Calculate utilization percentage
    const areaUtilization = summaryData.totalArea / (numBargesRequired * workableAreaWithStowage) * 100;
    const efficiency = (loadlinePercentage + areaUtilization) / 2;
    return {
      ...barge,
      numBargesRequired,
      limitingFactor,
      loadlinePercentage: loadlinePercentage.toFixed(1),
      areaUtilization: areaUtilization.toFixed(1),
      efficiency: efficiency.toFixed(1),
      workableArea: workableArea.toFixed(2),
      workableAreaWithStowage: workableAreaWithStowage.toFixed(2),
      weightLimitingQuantity: weightLimitingQuantity.toFixed(2),
      areaLimitingQuantity: areaLimitingQuantity.toFixed(2)
    };
  };
  // Calculate results for all barges and sort by efficiency
  const bargeResults = bargeDB.map(calculateBargeResults).sort((a, b) => a.numBargesRequired - b.numBargesRequired || parseFloat(b.efficiency) - parseFloat(a.efficiency));
  // Find the most efficient option
  const mostEfficientOption = bargeResults[0];
  const toggleExpand = bargeId => {
    if (expandedBarge === bargeId) {
      setExpandedBarge(null);
    } else {
      setExpandedBarge(bargeId);
    }
  };
  return <div className="space-y-6" data-id="element-159">
      <div className="flex justify-between items-center mb-4" data-id="element-160">
        <h3 className="text-xl font-semibold" data-id="element-161">Barge Options Comparison</h3>
        <Button onClick={() => {}} // Implement export
      variant="outline" className="flex items-center gap-2 border-blue-200 text-blue-700" data-id="element-162">
          <Download className="h-4 w-4" data-id="element-163" />
          Export Results
        </Button>
      </div>
      {/* Clickable Barge Comparison Table */}
      <div className="relative rounded-lg border border-blue-100 mb-8" data-id="element-164">
        <div className="overflow-x-auto" data-id="element-165">
          <Table data-id="element-166">
            <TableHeader className="bg-blue-50" data-id="element-167">
              <TableRow data-id="element-168">
                <TableHead className="w-[250px]" data-id="element-169">Barge Type</TableHead>
                <TableHead className="text-center" data-id="element-170">Barges Required</TableHead>
                <TableHead className="text-center" data-id="element-171">Limiting Factor</TableHead>
                <TableHead className="text-center" data-id="element-172">
                  Weight Utilization
                </TableHead>
                <TableHead className="text-center" data-id="element-173">Area Utilization</TableHead>
                <TableHead className="text-center" data-id="element-174">Dimensions (m)</TableHead>
                <TableHead className="text-center" data-id="element-175">Capacity (mT)</TableHead>
                <TableHead className="w-[50px]" data-id="element-176"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-177">
              {bargeResults.map(barge => <Fragment key={barge.id} data-id="element-178">
                  <TableRow className={`${barge.id === mostEfficientOption.id ? 'bg-blue-50 font-medium' : ''} cursor-pointer hover:bg-blue-50/50`} onClick={() => toggleExpand(barge.id)} data-id="element-179">
                    <TableCell className="font-medium" data-id="element-180">
                      {barge.name}
                      {barge.id === mostEfficientOption.id && <Badge className="ml-2" data-id="element-181">Recommended</Badge>}
                    </TableCell>
                    <TableCell className="text-center font-bold text-lg" data-id="element-182">
                      {barge.numBargesRequired}
                    </TableCell>
                    <TableCell className="text-center" data-id="element-183">
                      {barge.limitingFactor}
                    </TableCell>
                    <TableCell className="text-center" data-id="element-184">
                      {barge.loadlinePercentage}%
                    </TableCell>
                    <TableCell className="text-center" data-id="element-185">
                      {barge.areaUtilization}%
                    </TableCell>
                    <TableCell className="text-center" data-id="element-186">
                      {barge.workableLength.toFixed(1)} ×{' '}
                      {barge.workableWidth.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-center" data-id="element-187">
                      {barge.loadlineCapacity.toFixed(0)}
                    </TableCell>
                    <TableCell className="text-center" data-id="element-188">
                      {expandedBarge === barge.id ? <ChevronUp className="h-5 w-5 text-blue-700" data-id="element-189" /> : <ChevronDown className="h-5 w-5 text-blue-700" data-id="element-190" />}
                    </TableCell>
                  </TableRow>
                  {expandedBarge === barge.id && <TableRow className="bg-blue-50/50" data-id="element-191">
                      <TableCell colSpan={8} className="p-4 border-t border-blue-100" data-id="element-192">
                        <div className="flex flex-col md:flex-row gap-4 items-center bg-white p-4 rounded-lg border border-blue-100" data-id="element-193">
                          <div className="w-full md:w-1/3 rounded-md overflow-hidden" data-id="element-194">
                            <img src={barge.image} alt={barge.name} className="w-full h-48 object-cover" data-id="element-195" />
                          </div>
                          <div className="w-full md:w-2/3" data-id="element-196">
                            <h3 className="text-xl font-bold text-blue-900 mb-1" data-id="element-197">
                              {barge.name}
                            </h3>
                            <p className="text-blue-700 mb-4" data-id="element-198">
                              {barge.description}
                            </p>
                            <div className="grid grid-cols-2 gap-y-2 gap-x-4" data-id="element-199">
                              <div data-id="element-200">
                                <span className="text-blue-600 text-sm" data-id="element-201">
                                  Barges Required:
                                </span>
                                <p className="text-2xl font-bold text-blue-900" data-id="element-202">
                                  {barge.numBargesRequired}
                                </p>
                              </div>
                              <div data-id="element-203">
                                <span className="text-blue-600 text-sm" data-id="element-204">
                                  Limiting Factor:
                                </span>
                                <p className="font-medium" data-id="element-205">
                                  {barge.limitingFactor}
                                </p>
                              </div>
                              <div data-id="element-206">
                                <span className="text-blue-600 text-sm" data-id="element-207">
                                  Weight Utilization:
                                </span>
                                <p className="font-medium" data-id="element-208">
                                  {barge.loadlinePercentage}%
                                </p>
                              </div>
                              <div data-id="element-209">
                                <span className="text-blue-600 text-sm" data-id="element-210">
                                  Area Utilization:
                                </span>
                                <p className="font-medium" data-id="element-211">
                                  {barge.areaUtilization}%
                                </p>
                              </div>
                              <div data-id="element-212">
                                <span className="text-blue-600 text-sm" data-id="element-213">
                                  Dimensions:
                                </span>
                                <p className="font-medium" data-id="element-214">
                                  {barge.workableLength.toFixed(1)}m ×{' '}
                                  {barge.workableWidth.toFixed(1)}m
                                </p>
                              </div>
                              <div data-id="element-215">
                                <span className="text-blue-600 text-sm" data-id="element-216">
                                  Capacity:
                                </span>
                                <p className="font-medium" data-id="element-217">
                                  {barge.loadlineCapacity.toFixed(0)} mT
                                </p>
                              </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-blue-100" data-id="element-218">
                              <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-219">
                                <TooltipProvider data-id="element-220">
                                  <Tooltip data-id="element-221">
                                    <TooltipTrigger asChild data-id="element-222">
                                      <div className="flex items-center justify-between bg-blue-100 px-3 py-2 rounded" data-id="element-223">
                                        <span className="font-medium" data-id="element-224">
                                          Weight Utilization:
                                        </span>
                                        <span className="font-bold" data-id="element-225">
                                          {barge.loadlinePercentage}%
                                        </span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent data-id="element-226">
                                      <p data-id="element-227">Weight utilization per barge</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider data-id="element-228">
                                  <Tooltip data-id="element-229">
                                    <TooltipTrigger asChild data-id="element-230">
                                      <div className="flex items-center justify-between bg-blue-100 px-3 py-2 rounded" data-id="element-231">
                                        <span className="font-medium" data-id="element-232">
                                          Area Utilization:
                                        </span>
                                        <span className="font-bold" data-id="element-233">
                                          {barge.areaUtilization}%
                                        </span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent data-id="element-234">
                                      <p data-id="element-235">Deck area utilization per barge</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>}
                </Fragment>)}
            </TableBody>
          </Table>
        </div>
      </div>
      {/* Contact Form */}
      <div className="mt-8" data-id="element-236">
        <ContactForm calculatorData={formData} data-id="element-237" />
      </div>
      {/* Floating Button */}
      {showScrollButton && <div className="fixed bottom-8 right-8 z-50" data-id="element-238">
          <Button onClick={scrollToContactForm} className="bg-[#ffd700] text-[#1a365d] hover:bg-[#ffd700]/90 font-bold shadow-lg rounded-full px-4 py-6 flex items-center gap-2" data-id="element-239">
            Want the Fully Engineered Plan?
            <ArrowDown className="h-4 w-4" data-id="element-240" />
          </Button>
        </div>}
    </div>;
}